import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, Observable, tap, throwError } from "rxjs";
import { ExceptionService } from "src/app/shared/services/exception.service";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import {
  ExternalEndpoint,
  ExternalSystemCommunicationStateService,
} from "src/app/shared/services/external-system-communication-state.service";
import { NotificationCoreService } from "src/app/shared/services/notification/core/notification-core.service";
import { NotificationService } from "src/app/shared/services/notification/notification.service";
import { QuoteHttpService } from "./quote-http.service";

/**
 * The {@link QuoteHttpService} should only be called via effects from our store. This way we can guarantee the the following concerns are being handled:
 * - Integration with the {@link ExternalSystemCommunicationStateService} (e.g to display the spinner when there is a pending http request)
 * - Integration with the {@link NotificationService} to publish notifications (e.g when an exception occurred)
 *
 * In cases where we do not want to go via our store, we should handle the missing responsibilities via this service.
 */
@Injectable({
  providedIn: "root",
})
export class QuoteHttpFacadeService {
  constructor(
    private readonly _quoteHttpService: QuoteHttpService,
    private readonly _externalSystemCommunicationStateService: ExternalSystemCommunicationStateService,
    private readonly _notificationService: NotificationService,
    private readonly _exceptionService: ExceptionService,
  ) {}

  getProduct(quoteId: string) {
    this._externalSystemCommunicationStateService.setPendingRequest(
      ExternalEndpoint.QUOTES_GET_PRODUCT,
    );

    return this._quoteHttpService
      .getProduct(quoteId)
      .pipe(
        this.handleSuccess(ExternalEndpoint.QUOTES_GET_PRODUCT),
        this.handleError(ExternalEndpoint.QUOTES_GET_PRODUCT),
      );
  }

  private handleSuccess<T>(externalEndpoint: ExternalEndpoint) {
    return tap<T>({
      next: () => {
        this._externalSystemCommunicationStateService.unsetPendingRequest(
          externalEndpoint,
        );
      },
    });
  }

  private handleError<T>(externalEndpoint: ExternalEndpoint) {
    return catchError<T, Observable<never>>(
      (httpErrorResponse: HttpErrorResponse) => {
        this._externalSystemCommunicationStateService.unsetPendingRequest(
          externalEndpoint,
        );

        const exception =
          this._exceptionService.getException(httpErrorResponse);

        const notificationMessagePayLoad =
          NotificationCoreService.createHttpNotificationMessage(exception);

        this._notificationService.createNotificationMessage(
          notificationMessagePayLoad,
        );

        // We rethrow the error so that we also give the consumer a change to handle the exception
        return throwError(() => httpErrorResponse);
      },
    );
  }
}
